import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Layout
import { DashboardLayout } from './components/layout/DashboardLayout';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';

// Employee Pages
import VoucherListPage from './pages/employee/VoucherListPage';
import CreateVoucherPage from './pages/employee/CreateVoucherPage';
import EditVoucherPage from './pages/employee/EditVoucherPage';
import VoucherDetailPage from './pages/employee/VoucherDetailPage';
import DepartmentVouchersPage from './pages/employee/DepartmentVouchersPage';
import AddLogoToPdfPage from './pages/employee/AddLogoToPdfPage';

// Manager Pages
import ManagerApprovalsPage from './pages/manager/ManagerApprovalsPage';
import ManagerReviewPage from './pages/manager/ManagerReviewPage';
import ManagerAllVouchersPage from './pages/manager/ManagerAllVouchersPage';

// Accounts Pages
import AccountsQueuePage from './pages/accounts/AccountsQueuePage';
import AccountsProcessPage from './pages/accounts/AccountsProcessPage';

// Internal Audit Pages
import InternalAuditQueuePage from './pages/internal-audit/InternalAuditQueuePage';
import InternalAuditProcessPage from './pages/internal-audit/InternalAuditProcessPage';
import InternalAuditAllVouchersPage from './pages/internal-audit/InternalAuditAllVouchersPage';

// Finance Head Pages
import FinanceHeadQueuePage from './pages/finance-head/FinanceHeadQueuePage';
import FinanceHeadAllVouchersPage from './pages/finance-head/FinanceHeadAllVouchersPage';
import FinanceHeadRejectedVouchersPage from './pages/finance-head/FinanceHeadRejectedVouchersPage';
import FinanceHeadVoucherDetailPage from './pages/finance-head/FinanceHeadVoucherDetailPage';

// Admin Pages
import UserManagementPage from './pages/admin/UserManagementPage';
import AdminVouchersPage from './pages/admin/AdminVouchersPage';
import DepartmentManagementPage from './pages/admin/DepartmentManagementPage';
import AuditLogsPage from './pages/admin/AuditLogsPage';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0047AB]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <DashboardLayout>{children}</DashboardLayout>;
};

// App Routes
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />

      {/* Dashboard - All authenticated users */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />

      {/* Employee Routes */}
      <Route
        path="/vouchers"
        element={
          <ProtectedRoute allowedRoles={['employee', 'manager', 'finance_head', 'admin']}>
            <VoucherListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vouchers/new"
        element={
          <ProtectedRoute allowedRoles={['employee', 'manager', 'finance_head', 'admin']}>
            <CreateVoucherPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vouchers/:id"
        element={
          <ProtectedRoute allowedRoles={['employee', 'manager', 'finance_head', 'internal_audit', 'accounts', 'admin']}>
            <VoucherDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vouchers/:id/edit"
        element={
          <ProtectedRoute allowedRoles={['employee', 'manager', 'finance_head', 'admin']}>
            <EditVoucherPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/department-vouchers"
        element={
          <ProtectedRoute allowedRoles={['employee', 'manager', 'finance_head', 'admin']}>
            <DepartmentVouchersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pdf-tools"
        element={
          <ProtectedRoute allowedRoles={['employee', 'manager', 'accounts', 'internal_audit', 'finance_head', 'admin']}>
            <AddLogoToPdfPage />
          </ProtectedRoute>
        }
      />

      {/* Manager Routes */}
      <Route
        path="/approvals"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin']}>
            <ManagerApprovalsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/vouchers"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin']}>
            <ManagerAllVouchersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/vouchers/:id"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin']}>
            <ManagerReviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/approvals/:id"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin']}>
            <ManagerReviewPage />
          </ProtectedRoute>
        }
      />

      {/* Accounts Routes */}
      <Route
        path="/accounts/queue"
        element={
          <ProtectedRoute allowedRoles={['accounts', 'admin']}>
            <AccountsQueuePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounts/all"
        element={
          <ProtectedRoute allowedRoles={['accounts', 'admin']}>
            <AccountsQueuePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounts/vouchers"
        element={
          <ProtectedRoute allowedRoles={['accounts', 'admin']}>
            <VoucherListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounts/vouchers/new"
        element={
          <ProtectedRoute allowedRoles={['accounts', 'admin']}>
            <CreateVoucherPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounts/vouchers/:id"
        element={
          <ProtectedRoute allowedRoles={['accounts', 'admin']}>
            <VoucherDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounts/process/:id"
        element={
          <ProtectedRoute allowedRoles={['accounts', 'admin']}>
            <AccountsProcessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounts/vouchers/:id/edit"
        element={
          <ProtectedRoute allowedRoles={['accounts', 'admin']}>
            <EditVoucherPage />
          </ProtectedRoute>
        }
      />

      {/* Internal Audit Routes */}
      <Route
        path="/internal-audit/queue"
        element={
          <ProtectedRoute allowedRoles={['internal_audit', 'admin']}>
            <InternalAuditQueuePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/internal-audit/vouchers/:id"
        element={
          <ProtectedRoute allowedRoles={['internal_audit', 'admin']}>
            <InternalAuditProcessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/internal-audit/all"
        element={
          <ProtectedRoute allowedRoles={['internal_audit', 'admin']}>
            <InternalAuditAllVouchersPage />
          </ProtectedRoute>
        }
      />

      {/* Finance Head Routes */}
      <Route
        path="/finance-head/queue"
        element={
          <ProtectedRoute allowedRoles={['finance_head', 'admin']}>
            <FinanceHeadQueuePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance-head/all"
        element={
          <ProtectedRoute allowedRoles={['finance_head', 'admin']}>
            <FinanceHeadAllVouchersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance-head/rejected"
        element={
          <ProtectedRoute allowedRoles={['finance_head', 'admin']}>
            <FinanceHeadRejectedVouchersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance-head/vouchers/:id"
        element={
          <ProtectedRoute allowedRoles={['finance_head', 'admin']}>
            <FinanceHeadVoucherDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <UserManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/departments"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <DepartmentManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/vouchers"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminVouchersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/audit-logs"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AuditLogsPage />
          </ProtectedRoute>
        }
      />

      {/* Default Redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster
          position="top-center"
          toastOptions={{
            style: {
              background: 'white',
              border: '1px solid #e5e5e5',
              borderRadius: '8px',
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
